import dayjs from 'dayjs';

import { type ClientConfig, getClientConfig } from 'config/clients';

type Dayjs = ReturnType<typeof dayjs>;

/**
 * Moment is deprecated, please use the Intl API
 * Luxon is ok to use for localised formatting (i.e. by using tokens), but those should always be defined via client config
 */

const defaultLocale = getClientConfig()?.defaultLocale || window.navigator.language || undefined;

export type Format = Intl.DateTimeFormatOptions | keyof ClientConfig['formats'];

export const isValidDate = (d: Date) => d instanceof Date && !isNaN(d.getTime());

const defaultScheduleFormat: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  weekday: 'short',
  month: 'short',
  timeZoneName: 'short',
};
export const todsScheduleToDayjs = (
  schedule: { scheduledDate?: string; scheduledTime?: string },
  zone?: string,
): Dayjs | undefined => {
  if (!schedule?.scheduledDate) return undefined;
  return dayjs.tz(
    schedule.scheduledTime
      ? `${schedule.scheduledDate?.split('T')[0]}T${
          schedule.scheduledTime?.split('Z')[0].split('T').reverse()?.[0] ?? ''
        }`
      : schedule.scheduledDate?.split('Z')[0],
    zone ?? 'UTC',
  );
};

export const todsScheduleToFormat = (
  schedule: { scheduledDate?: string; scheduledTime?: string },
  zone?: string,
  format: Format = defaultScheduleFormat,
  unknown = '-',
): string => {
  if (!schedule?.scheduledDate || !format) return unknown;
  const dateTime = todsScheduleToDayjs(schedule, zone ?? 'UTC');
  return formatDateTime(dateTime, format, zone, defaultLocale, unknown);
};

export const formatDateTime = (
  input: Dayjs | string | Date,
  format: Format,
  zone?: string,
  locale = defaultLocale,
  unknown = 'Unknown',
): string => {
  const dateTime = typeof input === 'string' || input instanceof Date ? dayjs.tz(input, zone ?? 'UTC') : input;
  if (!dateTime?.isValid) return unknown;
  if (typeof format === 'string') return dateTime.format(getClientConfig()?.formats?.[format as string]);

  return new Intl.DateTimeFormat(locale, { ...(zone && { timeZone: zone }), ...format }).format(dateTime.toDate());
};

export const dayjsToFactorySafeTime = (dayjs: Dayjs) => {
  return dayjs.format('YYYY-MM-DDTHH:mm:ss.SSS');
};

export const factorySafeTimeToDayjs = (dateStr: Date | string, timeZone = 'UTC'): Dayjs => {
  if (!dateStr || dateStr === 'undefined') return undefined;
  // Override UTC timestamp with our own timezone without affecting the time
  return dayjs.tz(dayjs.tz(dateStr, 'UTC').format('YYYY-MM-DDTHH:mm:ss.SSS'), timeZone);
};

export const zonedDayjsDate = (date: Date | string, timeZone: string = 'UTC') => {
  if (!date || date === 'undefined') return undefined;
  return dayjs.utc(date).tz(timeZone);
};

export const subtractYearsFromDate = (date: Date, yearsToSubtract: number): Date => {
  const newDate = new Date(date);
  newDate.setFullYear(date.getFullYear() - yearsToSubtract);
  return newDate;
};

export const combineDayjsDateAndTime = (date: Dayjs, time: Dayjs, timezone = 'UTC'): Dayjs => {
  if (!date || !time) return undefined;
  return dayjs.tz(`${date.format('YYYY-MM-DD')}T${time.format('HH:mm:ss')}`, timezone);
};
